/**
 * Minimal custom date-range picker.
 * Uses event delegation — no per-cell listeners, no innerHTML replacement on hover.
 */

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_HEADERS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function pad2(n: number): string { return n < 10 ? '0' + n : String(n); }

function isoDate(y: number, m: number, d: number): string {
    return `${y}-${pad2(m + 1)}-${pad2(d)}`;
}

function todayIso(): string {
    const d = new Date();
    return isoDate(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatDisplay(iso: string): string {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
}

export interface DateRangePickerLocale {
    months?: string[];
    days?: string[];
    apply?: string;
    clear?: string;
    placeholder?: string;
    selectStart?: string;
    prevMonth?: string;
    nextMonth?: string;
}

export interface DateRangePickerOptions {
    onChange?: (from: string | null, to: string | null) => void;
    initialFrom?: string | null;
    initialTo?: string | null;
    locale?: DateRangePickerLocale;
}

export class DateRangePicker {
    private trigger: HTMLElement;
    private opts: DateRangePickerOptions;
    private popover: HTMLElement | null = null;
    private _from: string | null = null;
    private _to: string | null = null;
    private hovered: string | null = null;
    private awaitingEnd = false;
    private viewYear: number;
    private viewMonth: number;
    private clickHandler: () => void;
    private outsideHandler: (e: MouseEvent) => void;
    private keydownHandler: (e: KeyboardEvent) => void;

    private get locMonths(): string[] { return this.opts.locale?.months ?? MONTHS; }
    private get locDays(): string[] { return this.opts.locale?.days ?? DAY_HEADERS; }
    private get locApply(): string { return this.opts.locale?.apply ?? 'Apply'; }
    private get locClear(): string { return this.opts.locale?.clear ?? 'Clear'; }
    private get locPlaceholder(): string { return this.opts.locale?.placeholder ?? 'Select dates'; }
    private get locSelectStart(): string { return this.opts.locale?.selectStart ?? 'Select start date'; }
    private get locPrevMonth(): string { return this.opts.locale?.prevMonth ?? 'Previous month'; }
    private get locNextMonth(): string { return this.opts.locale?.nextMonth ?? 'Next month'; }

    constructor(trigger: HTMLElement, opts: DateRangePickerOptions = {}) {
        this.trigger = trigger;
        this.opts = opts;
        const now = new Date();
        this.viewYear = now.getFullYear();
        this.viewMonth = now.getMonth();

        this._from = opts.initialFrom ?? null;
        this._to   = opts.initialTo   ?? null;

        this.clickHandler = () => this.toggle();
        this.outsideHandler = (e: MouseEvent) => {
            if (!this.popover) return;
            if (!this.popover.contains(e.target as Node) && !this.trigger.contains(e.target as Node)) {
                this.close();
            }
        };
        this.keydownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.popover) this.close();
        };

        trigger.addEventListener('click', this.clickHandler);
        this.updateTrigger();
    }

    get from(): string | null { return this._from; }
    get to(): string | null { return this._to; }

    reset(): void {
        this._from = null;
        this._to = null;
        this.awaitingEnd = false;
        this.hovered = null;
        this.updateTrigger();
        if (this.popover) this.updateDayClasses();
    }

    destroy(): void {
        this.close();
        this.trigger.removeEventListener('click', this.clickHandler);
    }

    private toggle(): void {
        if (this.popover) { this.close(); return; }
        this.open();
    }

    private open(): void {
        this.popover = document.createElement('div');
        this.popover.className = 'drp-popover';
        this.buildPopover();

        // Single delegated click handler — no per-cell listeners, no re-render on hover
        this.popover.addEventListener('click', (e) => {
            e.stopPropagation(); // always stop — prevents outsideHandler from firing for inner clicks

            const target = e.target as HTMLElement;

            const day = target.closest<HTMLElement>('.drp-day');
            if (day?.dataset.date) { this.handleDayClick(day.dataset.date); return; }

            if (target.closest('.drp-prev')) { this.shiftMonth(-1); return; }
            if (target.closest('.drp-next')) { this.shiftMonth(1); return; }

            if (target.closest('.drp-clear')) {
                this.reset();
                this.opts.onChange?.(null, null);
                return;
            }

            if (target.closest('.drp-apply')) {
                if (this._from && this._to) {
                    this.opts.onChange?.(this._from, this._to);
                    this.close();
                }
                return;
            }
        });

        // Hover preview — update classes only, no HTML replacement
        this.popover.addEventListener('mouseover', (e) => {
            if (!this.awaitingEnd) return;
            const day = (e.target as HTMLElement).closest<HTMLElement>('.drp-day');
            const date = day?.dataset.date ?? null;
            if (date !== this.hovered) {
                this.hovered = date;
                this.updateDayClasses();
            }
        });

        this.popover.addEventListener('mouseleave', () => {
            if (this.awaitingEnd && this.hovered !== null) {
                this.hovered = null;
                this.updateDayClasses();
            }
        });

        document.body.appendChild(this.popover);
        this.positionPopover();
        this.trigger.classList.add('drp-open');
        setTimeout(() => {
            document.addEventListener('click', this.outsideHandler);
            document.addEventListener('keydown', this.keydownHandler);
        }, 0);
    }

    private close(): void {
        this.popover?.remove();
        this.popover = null;
        this.hovered = null;
        this.awaitingEnd = false;
        this.trigger.classList.remove('drp-open');
        document.removeEventListener('click', this.outsideHandler);
        document.removeEventListener('keydown', this.keydownHandler);
    }

    private shiftMonth(delta: number): void {
        this.viewMonth += delta;
        if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear--; }
        if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear++; }
        this.buildPopover(); // full re-render only for month navigation
    }

    private positionPopover(): void {
        if (!this.popover) return;
        const rect = this.trigger.getBoundingClientRect();
        const scrollY = window.scrollY, scrollX = window.scrollX;
        const popH = this.popover.offsetHeight || 320;
        const spaceBelow = window.innerHeight - rect.bottom;
        const top = spaceBelow >= popH + 8
            ? rect.bottom + scrollY + 6
            : rect.top + scrollY - popH - 6;
        const maxLeft = window.innerWidth + scrollX - (this.popover.offsetWidth || 520) - 12;
        this.popover.style.top = `${top}px`;
        this.popover.style.left = `${Math.max(Math.min(rect.left + scrollX, maxLeft), 8)}px`;
    }

    // Full DOM build — called only on open and on month navigation
    private buildPopover(): void {
        if (!this.popover) return;
        const m1y = this.viewYear, m1m = this.viewMonth;
        const m2m = (m1m + 1) % 12, m2y = m1m === 11 ? m1y + 1 : m1y;

        this.popover.innerHTML = `
            <div class="drp-months">
                ${this.renderMonth(m1y, m1m, true, false)}
                ${this.renderMonth(m2y, m2m, false, true)}
            </div>
            <div class="drp-footer">
                <span class="drp-range-display text-muted small">${this.rangeText()}</span>
                <button type="button" class="btn btn-sm btn-outline-secondary drp-clear">${this.locClear}</button>
                <button type="button" class="btn btn-sm btn-primary drp-apply" ${(!this._from || !this._to) ? 'disabled' : ''}>${this.locApply}</button>
            </div>
        `;
    }

    // Patch only classes + footer text + apply state — never touches innerHTML
    private updateDayClasses(): void {
        if (!this.popover) return;
        const today = todayIso();
        this.popover.querySelectorAll<HTMLElement>('.drp-day').forEach(cell => {
            const date = cell.dataset.date!;
            cell.className = ['drp-day', ...this.dayClasses(date, today)].join(' ');
        });
        const applyBtn = this.popover.querySelector<HTMLButtonElement>('.drp-apply');
        if (applyBtn) applyBtn.disabled = !this._from || !this._to;
        const rangeDisplay = this.popover.querySelector('.drp-range-display');
        if (rangeDisplay) rangeDisplay.textContent = this.rangeText();
    }

    private renderMonth(year: number, month: number, showPrev: boolean, showNext: boolean): string {
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const today = todayIso();

        let html = `<div class="drp-month">
            <div class="drp-month-header">
                ${showPrev ? `<button type="button" class="drp-nav drp-prev" aria-label="${this.locPrevMonth}">‹</button>` : '<span></span>'}
                <span class="drp-month-label">${this.locMonths[month]} ${year}</span>
                ${showNext ? `<button type="button" class="drp-nav drp-next" aria-label="${this.locNextMonth}">›</button>` : '<span></span>'}
            </div>
            <div class="drp-day-headers">${this.locDays.map(d => `<span>${d}</span>`).join('')}</div>
            <div class="drp-days">`;

        for (let i = 0; i < firstDay; i++) html += '<span class="drp-day-blank"></span>';

        for (let d = 1; d <= daysInMonth; d++) {
            const date = isoDate(year, month, d);
            const classes = ['drp-day', ...this.dayClasses(date, today)];
            html += `<button type="button" class="${classes.join(' ')}" data-date="${date}">${d}</button>`;
        }

        html += '</div></div>';
        return html;
    }

    private dayClasses(date: string, today: string): string[] {
        const cls: string[] = [];
        const from = this._from, to = this._to;
        const hov = this.hovered;

        // Finalized range
        if (from && to) {
            if (date === from) cls.push('drp-start', 'drp-end-cap');
            else if (date === to) cls.push('drp-end', 'drp-end-cap');
            else if (date > from && date < to) cls.push('drp-in-range');
        } else if (from && !to) {
            if (date === from && !this.awaitingEnd) {
                cls.push('drp-start', 'drp-end-cap');
            }
            // Hover preview while awaiting end
            if (this.awaitingEnd && hov) {
                const lo = from < hov ? from : hov;
                const hi = from < hov ? hov : from;
                if (date === lo) cls.push('drp-start', 'drp-end-cap');
                else if (date === hi) cls.push('drp-end', 'drp-end-cap');
                else if (date > lo && date < hi) cls.push('drp-in-range');
                if (date === from && lo === from) {
                    // already pushed above
                }
            } else if (!this.awaitingEnd) {
                // already handled above
            }
        }

        if (date === today && !cls.some(c => c.startsWith('drp-start') || c.startsWith('drp-end'))) {
            cls.push('drp-today');
        }
        return cls;
    }

    private handleDayClick(date: string): void {
        if (!this.awaitingEnd) {
            this._from = date;
            this._to = null;
            this.awaitingEnd = true;
        } else {
            if (date >= this._from!) {
                this._to = date;
            } else {
                this._to = this._from;
                this._from = date;
            }
            this.awaitingEnd = false;
            this.hovered = null;
        }
        this.updateTrigger();
        this.updateDayClasses(); // patch classes only — no innerHTML replacement
    }

    private rangeText(): string {
        if (this._from && this._to) return `${formatDisplay(this._from)} → ${formatDisplay(this._to)}`;
        if (this._from) return `${formatDisplay(this._from)} → ...`;
        return this.locSelectStart;
    }

    private updateTrigger(): void {
        if (this._from && this._to) {
            this.trigger.classList.add('drp-has-value');
            this.trigger.textContent = `${formatDisplay(this._from)} → ${formatDisplay(this._to)}`;
        } else {
            this.trigger.classList.remove('drp-has-value');
            this.trigger.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="14" height="14" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" /></svg> <span class="drp-placeholder">${this.locPlaceholder}</span> <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="12" height="12" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>`;
        }
    }
}
